import { FunctionComponent, JSX, useEffect, useState } from 'react'
import styles from '@/container/daycare/daycareProfile/meal/meal.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useTranslation } from 'react-i18next'
import { MealMenu } from '@/types/Meal.ts'
import { GET_DAYCARE_MEAL_MENUS } from '@/modules/API/meal/getMealDaycareSettings.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { Chip, ListItem } from '@mui/material'
import { Delete, Edit } from '@mui/icons-material'
import { useLazyQuery } from '@apollo/client'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'
import AddMealMenuDialog from '@/container/daycare/daycareProfile/meal/mealMenus/AddMealMenuDialog.tsx'
import UpdateMealMenuDialog from '@/container/daycare/daycareProfile/meal/mealMenus/UpdateMealMenuDialog.tsx'
import ConfirmDeleteMealMenuDialog from '@/container/daycare/daycareProfile/meal/mealMenus/ConfirmDeleteMealMenuDialog.tsx'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { useDaycareRight } from '@/hooks/useDaycareRight'

const MealMenus: FunctionComponent = () => {
    const { t } = useTranslation()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const canUpdate: boolean = useDaycareRight('canWriteParamsDaycare')

    const [addMealMenuDialog, setAddMealMenuDialog] = useState<boolean>(false)
    const [editMenu, setEditMenu] = useState<MealMenu | null>(null)
    const [deleteMenu, setDeleteMenu] = useState<MealMenu | null>(null)

    const [menus, setMenus] = useState<MealMenu[]>([])

    const [getMealMenus, { loading, data, error, refetch }] = useLazyQuery(GET_DAYCARE_MEAL_MENUS, {
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (data) {
            setMenus(data.getDaycareMealMenus)
        }
    }, [data])

    useEffect(() => {
        getMealMenus()
    }, [daycare.id])

    return (
        <div className={styles.BlockPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myMenus')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddMealMenuDialog(true)}
                        />
                    )}
                </div>
                {loading && <Loading overall={false} />}
                {error && <ErrorMessage error={t('generalError')} />}
                {menus && menus.length > 0 ? (
                    <div className={styles.InputContainer}>
                        {[...menus]
                            .sort((a: MealMenu, b: MealMenu): number => a.menu.localeCompare(b.menu))
                            .map(
                                (data: MealMenu): JSX.Element => (
                                    <ListItem key={data.id} className={styles.ListItem}>
                                        <Chip
                                            className={styles.ChipMenu}
                                            label={
                                                <span className={styles.ChipLabel}>
                                                    <span>{data.menu}</span>
                                                    {canUpdate && (
                                                        <>
                                                            <Edit
                                                                className={styles.ChipActionIcon}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setEditMenu(data)
                                                                }}
                                                            />
                                                            <Delete
                                                                className={styles.ChipActionIcon}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setDeleteMenu(data)
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </span>
                                            }
                                        />
                                    </ListItem>
                                )
                            )}
                    </div>
                ) : (
                    <p>{t('noMealMenus')}</p>
                )}
            </div>
            <AddMealMenuDialog
                open={addMealMenuDialog}
                handleClose={() => setAddMealMenuDialog(false)}
                refetch={refetch}
                daycareId={daycare.id}
            />
            {editMenu && (
                <UpdateMealMenuDialog
                    open={!!editMenu}
                    handleClose={() => setEditMenu(null)}
                    refetch={refetch}
                    daycareId={daycare.id}
                    menu={editMenu}
                />
            )}
            {deleteMenu && (
                <ConfirmDeleteMealMenuDialog
                    open={!!deleteMenu}
                    handleClose={() => setDeleteMenu(null)}
                    daycareId={daycare.id}
                    menu={deleteMenu}
                    refetch={refetch}
                />
            )}
        </div>
    )
}

export default MealMenus
